/**
 * ImportConflictResolver - Duplicate conflict resolution dialog
 *
 * PLATFORM:F1.P9-10 Phase 4: Shows duplicates found during import.
 * User can skip, overwrite, or keep both for each conflict.
 */
import React, { useState } from 'react';
import { X, AlertTriangle, SkipForward, RefreshCw, Copy, CheckCircle2, Loader2 } from 'lucide-react';

const RESOLUTION_OPTIONS = [
  { id: 'skip', label: 'Skip', icon: SkipForward, description: 'Keep existing, ignore new' },
  { id: 'overwrite', label: 'Overwrite', icon: RefreshCw, description: 'Replace existing with new' },
  { id: 'keep_both', label: 'Keep Both', icon: Copy, description: 'Add as duplicate copy' },
];

/**
 * The SERVED `match_field` vocabulary of a preview duplicate row (DuplicateIndex.find in
 * app/services/vault_import_management_service.py): comic_issue_id | barcode | external_id |
 * title_issue. Anything else is rendered raw rather than invented.
 */
export const MATCH_FIELD_LABELS = {
  comic_issue_id: 'catalog issue',
  barcode: 'barcode',
  external_id: 'CLZ id',
  title_issue: 'title + issue number',
};

export function matchFieldLabel(matchField) {
  return MATCH_FIELD_LABELS[matchField] || matchField || 'unknown';
}

function incomingLabel(dup) {
  if (!dup.new_title) return null;
  return dup.new_issue_number ? `${dup.new_title} #${dup.new_issue_number}` : dup.new_title;
}

export default function ImportConflictResolver({
  isOpen,
  onClose,
  duplicates = [],
  onResolve,
  onResolveAll,
}) {
  const [resolutions, setResolutions] = useState({});
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(new Set());

  if (!isOpen || duplicates.length === 0) return null;

  const handleResolveOne = async (idx, resolution) => {
    setResolutions((prev) => ({ ...prev, [idx]: resolution }));
    setResolving(true);
    try {
      await onResolve?.(duplicates[idx], resolution);
      setResolved((prev) => new Set([...prev, idx]));
    } catch {
      // Error handled by parent
    } finally {
      setResolving(false);
    }
  };

  const handleResolveAll = async (resolution) => {
    setResolving(true);
    try {
      const unresolved = duplicates.filter((_, idx) => !resolved.has(idx));
      await onResolveAll?.(unresolved, resolution);
      const allIdxs = duplicates.map((_, idx) => idx);
      setResolved(new Set(allIdxs));
    } catch {
      // Error handled by parent
    } finally {
      setResolving(false);
    }
  };

  const unresolvedCount = duplicates.length - resolved.size;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      data-testid="conflict-resolver-dialog"
    >
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-yellow-400" />
            <h2 className="text-white font-semibold">
              {duplicates.length} Duplicate{duplicates.length > 1 ? 's' : ''} Found
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-zinc-800 transition-colors"
            aria-label="Close"
            data-testid="conflict-close"
          >
            <X size={18} className="text-zinc-400" />
          </button>
        </div>

        {/* Bulk actions */}
        {unresolvedCount > 1 && (
          <div className="px-4 py-2 border-b border-zinc-800 flex gap-2">
            <span className="text-zinc-400 text-xs self-center mr-2">Apply to all:</span>
            {RESOLUTION_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => handleResolveAll(opt.id)}
                disabled={resolving}
                className="px-3 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-xs transition-colors disabled:opacity-50"
                data-testid={`conflict-all-${opt.id}`}
              >
                {opt.label} All
              </button>
            ))}
          </div>
        )}

        {/* Duplicate list */}
        <div className="px-4 py-3 space-y-3 max-h-96 overflow-y-auto">
          {duplicates.map((dup, idx) => (
            <div
              key={idx}
              className={`rounded-xl border p-3 ${
                resolved.has(idx)
                  ? 'border-green-800/30 bg-green-900/10'
                  : 'border-zinc-800 bg-zinc-800/50'
              }`}
              data-testid={`conflict-item-${idx}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {dup.existing_title || 'Untitled'}
                    {dup.existing_issue_number ? ` #${dup.existing_issue_number}` : ''}
                  </p>
                  <p className="text-zinc-500 text-xs">
                    Matched by: {matchFieldLabel(dup.match_field)}
                  </p>
                  {incomingLabel(dup) && (
                    <p className="text-zinc-400 text-xs" data-testid={`conflict-${idx}-incoming`}>
                      Incoming: {incomingLabel(dup)}
                    </p>
                  )}
                </div>
                {resolved.has(idx) && (
                  <CheckCircle2 size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
                )}
              </div>

              {!resolved.has(idx) && (
                <div className="flex gap-2">
                  {RESOLUTION_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => handleResolveOne(idx, opt.id)}
                      disabled={resolving}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-lg text-xs transition-colors disabled:opacity-50"
                      data-testid={`conflict-${idx}-${opt.id}`}
                    >
                      <opt.icon size={12} />
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {resolved.has(idx) && (
                <p className="text-green-400 text-xs">
                  Resolved: {resolutions[idx]}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-zinc-800">
          {unresolvedCount === 0 ? (
            <button
              onClick={onClose}
              className="w-full bg-[#D4A03C] hover:bg-[#E0B04C] text-black font-semibold rounded-xl py-2.5 text-sm transition-colors"
              style={{ minHeight: '44px' }}
              data-testid="conflict-done"
            >
              Done - Continue Import
            </button>
          ) : (
            <p className="text-zinc-400 text-sm text-center">
              {unresolvedCount} conflict{unresolvedCount > 1 ? 's' : ''} remaining
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
